import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface MetaTagsProps {
  title?: {
    ar: string;
    en: string;
  };
  description?: {
    ar: string;
    en: string;
  };
  // Accept plain string keywords or structured for backward compatibility
  keywords?: {
    ar: string;
    en: string;
  };
  url?: string;
  image?: string;
  type?: string;
  // Raw seo_meta blob passed from page (optional) to simplify integration
  seoMetaRaw?: {
    title_ar?: string; title_en?: string; description_ar?: string; description_en?: string; image_url?: string;
  } | null;
}

const MetaTags = ({
  title = {
    ar: "أباسك",
    en: "Abask"
  },
  description = {
    ar: "أباسك - خبراء في التطوير العقاري والتجاري والصناعي منذ عقود. نبتكر المستقبل عبر مشاريعنا الرائدة وشراكاتنا الاستراتيجية.",
    en: "Abask - Experts in real estate, commercial and industrial development for decades. We innovate the future through our leading projects and strategic partnerships."
  },
  keywords = {
    ar: "أباسك، شركة أباسك، التطوير العقاري، التجاري، الصناعي، المملكة العربية السعودية، مشاريع، استثمار",
    en: "Abask, real estate development, commercial, industrial, Saudi Arabia, projects, investment"
  },
  url = "https://abask.g-ksa.com/",
  image = "https://abask.g-ksa.com/op-uploads/og-image.png",
  type = "website",
  seoMetaRaw
}: MetaTagsProps) => {
  const { language } = useLanguage();

  useEffect(() => {
    // Merge overrides (defensive trimming + length control)
    const mergedTitle = {
      ar: (seoMetaRaw?.title_ar || title.ar).trim().slice(0, 70),
      en: (seoMetaRaw?.title_en || title.en).trim().slice(0, 70)
    };
    const mergedDescription = {
      ar: (seoMetaRaw?.description_ar || description.ar).trim().slice(0, 160),
      en: (seoMetaRaw?.description_en || description.en).trim().slice(0, 160)
    };
    const currentTitle = language === 'ar' ? mergedTitle.ar : mergedTitle.en;
    const currentDescription = language === 'ar' ? mergedDescription.ar : mergedDescription.en;
    const currentKeywords = (language === 'ar' ? keywords.ar : keywords.en).slice(0, 250);

    // Compute canonical automatically if not provided (strip query + hash)
    let canonical = url;
    if (!canonical && typeof window !== 'undefined') {
      try {
        const u = new URL(window.location.href);
        u.hash = '';
        u.searchParams.delete('preview');
        u.searchParams.delete('dyn');
        canonical = u.toString();
      } catch { /* ignore */ }
    }

    if (currentTitle) document.title = currentTitle;

    const updateMetaTag = (name: string, content: string, property = false) => {
      if (!content) return;
      const attribute = property ? 'property' : 'name';
      let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, attrs: Record<string,string>) => {
      const selector = Object.entries(attrs).map(([k,v])=>`link[rel="${rel}"][${k}="${v}"]`).join(',');
      let link = document.head.querySelector(selector) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        Object.entries(attrs).forEach(([k,v])=>link!.setAttribute(k,v));
        document.head.appendChild(link);
      } else {
        Object.entries(attrs).forEach(([k,v])=>link!.setAttribute(k,v));
      }
    };

    // Primary
    updateMetaTag('title', currentTitle);
    updateMetaTag('description', currentDescription);
    updateMetaTag('keywords', currentKeywords);

    // OG / Twitter
    updateMetaTag('og:type', type, true);
    if (canonical) {
      updateMetaTag('og:url', canonical, true);
      updateMetaTag('twitter:url', canonical, true);
    }
    updateMetaTag('og:title', currentTitle, true);
    updateMetaTag('og:description', currentDescription, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', currentTitle, true);
    updateMetaTag('twitter:description', currentDescription, true);
    updateMetaTag('twitter:image', image, true);
    updateMetaTag('og:site_name', language === 'ar' ? 'أباسك' : 'Abask', true);
    updateMetaTag('og:locale', language === 'ar' ? 'ar_SA' : 'en_US', true);

    // Canonical + hreflang alternates (ar / en) if canonical available
    if (canonical) {
      const base = (() => { try { return new URL(canonical!).origin; } catch { return undefined; } })();
      updateLinkTag('canonical', { href: canonical });
      if (base) {
        updateLinkTag('alternate', { href: canonical.replace(/([?&].*)$/, ''), hreflang: language === 'ar' ? 'ar' : 'en' });
        // Generate alternate for the other language by toggling /ar or /en if simple; fallback same canonical
        const otherLang = language === 'ar' ? 'en' : 'ar';
        let otherUrl = canonical;
        if (canonical.includes('/ar/')) otherUrl = canonical.replace('/ar/', '/en/');
        else if (canonical.includes('/en/')) otherUrl = canonical.replace('/en/', '/ar/');
        updateLinkTag('alternate', { href: otherUrl, hreflang: otherLang });
        updateLinkTag('alternate', { href: canonical, hreflang: 'x-default' });
      }
    }

    // Additional tags
    updateMetaTag('author', language === 'ar' ? 'أباسك' : 'Abask');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', language);

    // Structured Data (JSON-LD) – Organization & Website (idempotent)
    const scriptId = 'ld-org-schema';
    let ld = document.getElementById(scriptId) as HTMLScriptElement | null;
    const orgData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Abask',
      alternateName: 'أباسك',
      url: canonical,
      logo: image,
      sameAs: [ canonical ]
    };
    if (!ld) {
      ld = document.createElement('script');
      ld.id = scriptId;
      ld.type = 'application/ld+json';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(orgData);
  }, [language, title, description, keywords, url, image, type, seoMetaRaw]);

  return null;
};

export default MetaTags;